import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { ChatContextItem } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { BrandInsightChatContexts, ChatContextType, isBrandInsightsContext } from '@trinity/chat';
import { Search, X, Home } from 'react-feather';
import { CapabilitiesContext, ChatViewType, ChatViewTypeContext, LinkContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext, ChatLandingCardsVisibleContext } from '@trinity/chat-components/components/Chat/context';
import { FilterButton } from '@trinity/chat-components/components/Chat.Filters';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { Constants } from '@trinity/chat-components/components/Theme';
import { ChatAppPlatformContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { trunc } from '@trinity/chat-components/utils';
import { AgentResourcesProvider, useAgentResources } from './AgentResources.Container';
import { ChatTabs } from './Chat.Tabs';
import { HeaderContextContext } from './context';
import { ContextSearch } from './ContextSearch';
import { ChatContextSearchContainer } from './ContextSearch.Container';
import { ContextSelectorContainer } from './ContextSelector.Container';
import { JumpToPage } from './JumpToPage';
import { SimpleContextSelector } from './SimpleContextSelector';
import { useIsFullPageChat } from '../Chat/hooks/useIsFullPageChat';
import { GenAIExternalPlatform } from '@trinity/management-app-client';

type Props = {
  className?: string;
};

function ChatHeader({ className }: Props) {
  const link = useContext(LinkContext);
  const capabilities = useContext(CapabilitiesContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();
  const navigate = useNavigate();
  const landingCardsVisible = useContext(ChatLandingCardsVisibleContext);
  const isFullPageChat = useIsFullPageChat();
  const viewMode = useContext(ChatViewTypeContext);
  const platform = useContext(ChatAppPlatformContext);

  const agentResources = useAgentResources();

  const handleHomeClick = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  const showFilterButton = useMemo(() => {

    if (session.context.type === ChatContextType.Agent) {
      if (BrandInsightChatContexts.includes(session.context.data.identifier)) {
        return true;
      }

      const hasAgentFilters = agentResources.data.filters.length > 0 || agentResources.data.dataSources.length > 0;
      return hasAgentFilters;
    }

    return true;
  }, [session.context.type]);

  const isLoading = useMemo(() => {
    return agentResources.isLoading && !link.initialized;
  }, [agentResources.isLoading, link.initialized]);

  const showTabs = viewMode === ChatViewType.Page;
  const showHomeButton = features.homeButton && isFullPageChat && platform === GenAIExternalPlatform.GenAIPlatform;
  const showContextSelector = !(features.chatContextSelector && landingCardsVisible);

  return (
    <Root className={className}>
      <Wrap>
        {!isLoading && (
          <>
            <TabBar>
              <Left>
                {showHomeButton && (
                  <Tooltip title="Home">
                    <StyledHomeButton onClick={handleHomeClick}>
                      <StyledHomeIcon size={20} />
                    </StyledHomeButton>
                  </Tooltip>
                )}
                {showFilterButton && <StyledFilterButton />}
                {showContextSelector && <SimpleContextSelector />}
                <ContextDisplay />
              </Left>

              {showTabs && <ChatTabs />}
            </TabBar>

            <Right>
              {capabilities.canViewExternalLinks && <JumpToPage />}
            </Right>
          </>
        )}
      </Wrap>
    </Root>
  );
}

function ContextDisplay() {
  const {
    pendingContextType,
    setPendingContextType,
    setPendingContext,
    pendingEverythingSearch,
    setPendingEverythingSearch,
  } = useContext(HeaderContextContext);
  const session = useContext(ActiveChatSessionContext);

  const handleReset = useCallback(() => {
    setPendingContextType(session.context.type);

    if (session.context.type === ChatContextType.Agent) {
      setPendingContext(session.context.data);
    } else {
      setPendingContext(null);
    }

  }, [setPendingContextType, session.context.type, session.context.data, setPendingContext]);

  const handleEverythingSearch = useCallback(() => {
    setPendingEverythingSearch(true);
  }, [setPendingEverythingSearch]);

  const handleAgentSubjectSearch = useCallback(() => {
    setPendingContextType(session.context.type);
    setPendingContext(session.context.data as ChatContextItem);
  }, [setPendingContextType, session.context.type, session.context.data, setPendingContext]);

  const displayName = useMemo(() => {
    return extractNameFromContext(session.context);
  }, [session.context]);

  const searchEnabled = useMemo(() => {
    return isBrandInsightsContext(session.context) ||
      (session.context.type === ChatContextType.Agent && !!session.context.data.subjectAutocomplete) ||
      session.context.type !== ChatContextType.Agent;
  }, [session.context]);

  const renderSubject = useCallback(() => {
    const name = displayName;

    if (name) {
      const handleClick = searchEnabled ? handleReset : undefined;

      const maxNameLength = 50;

      if (name?.length > maxNameLength) {
        return (
          <Tooltip title={name}>
            <Subject onClick={handleClick}>
              {trunc(name, maxNameLength)}
            </Subject>
          </Tooltip>
        );
      }

      return (
        <Subject onClick={handleClick}>
          {name}
        </Subject>
      );
    }
  }, [handleReset, displayName, searchEnabled]);

  const renderCloseIcon = useCallback(() => {
    if (session.context.type === ChatContextType.Agent) return null;

    return (
      <CloseIconWrapper onClick={handleReset}>
        <CloseIcon size={18} />
      </CloseIconWrapper>
    );
  }, [handleReset, session.context.type]);

  const hasActiveSearch = useMemo(() => {
    if (pendingEverythingSearch) return true;

    if (!pendingContextType) return false;

    return true;
  }, [pendingContextType, pendingEverythingSearch]);

  if (hasActiveSearch) {
    return (
      <ChatContextSearchContainer>
        <StyledContextSearch />
      </ChatContextSearchContainer>
    );
  }

  if (session.context.type === ChatContextType.Global) {
    return (
      <StyledSearchIcon
        size={19}
        onClick={handleEverythingSearch} />
    );
  }

  if (!displayName) {
    if (searchEnabled) {
      return (
        <StyledSearchIcon
          size={19}
          onClick={handleAgentSubjectSearch} />
      );
    }

    if (session.context.type === ChatContextType.KolProfile) {
      return (
        <StyledSearchIcon
          size={19}
          onClick={() => setPendingContextType(session.context.type)} />
      );
    }

    return null;
  }

  return (
    <ContextSubjectWrapper>
      {renderSubject()}
      {renderCloseIcon()}
    </ContextSubjectWrapper>
  );
}

function extractNameFromContext(context: Chat.InstanceContextWithMetadata): string | null {
  if (context.type === ChatContextType.Global) {
    return null;
  }

  if (context.type === ChatContextType.Agent) {
    if (context.data?.subject?.name) {
      return context.data.subject.name;
    }
  }

  const metadataName = context.metadata?.name;

  return typeof metadataName === 'string' ? metadataName : null;
}

function ChatHeaderWrapper() {
  return (
    <ContextSelectorContainer>
      <AgentResourcesProvider>
        <ChatHeader />
      </AgentResourcesProvider>
    </ContextSelectorContainer>
  );
}

export { ChatHeaderWrapper as ChatHeader };

const Root = styled.div(({ theme }) => ({
  position: 'sticky',
  top: '0',
  height: `${Constants.Modal.HeaderHeight}px`,
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.gray.light2}`,
  padding: '10px',
  background: theme.palette.white.main,
}));

const Wrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const TabBar = styled.div({
  display: 'flex',
  gap: '15px',
  minWidth: 0,
  flex: 1,
});

const Left = styled.div({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  minWidth: 0,
  flexShrink: 1,
});

const Right = styled.div({
  cursor: 'pointer',
  marginRight: '8px',
});

const StyledFilterButton = styled(FilterButton)({});

const StyledHomeButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));

const StyledHomeIcon = styled(Home)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const StyledContextSearch = styled(ContextSearch)({});

const ContextSubjectWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flexShrink: 1,
}));

const CloseIcon = styled(X)(({ theme }) => ({
  color: theme.palette.gray.main,
}));

const CloseIconWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  boxSizing: 'border-box',
  width: 24,
  height: 24,

  cursor: 'pointer',

  marginLeft: 10,

  borderRadius: '50%',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,

    '& svg': {
      color: theme.palette.black.main,
    },
  },
}));

const StyledSearchIcon = styled(Search)(({ theme }) => ({
  marginLeft: 8,
  cursor: 'pointer',
  color: theme.palette.black.main,
}));

const Subject = styled.div(({ theme }) => ({
  // cursor: 'pointer',
  padding: 5,
  boxSizing: 'border-box',
  borderRadius: 4,

  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: 'transparent',

  color: theme.palette.black.main,

  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  // '&:hover': {
  //   backgroundColor: theme.palette.gray.light2,
  // },
}));